/**
 * The spec §9 route table: each screen's path (from lib/nav.ts) and the page component that
 * renders it. App.tsx turns this into <Route>s inside the shell; brand screens get BrandRoute.
 *
 * This module only imports `react` (for lazy) and lib/nav.ts, so Node can import it too: the
 * Playwright smoke test reads ROUTE_PATHS from here.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { PAGES, type PageDef, type PageKey } from './lib/nav';

export { PAGES, ROUTE_PATHS, SAMPLE_BRAND_ID, pathFor, findPage } from './lib/nav';
export type { PageDef, PageKey } from './lib/nav';

type Page = LazyExoticComponent<ComponentType>;

export const PAGE_COMPONENTS: Record<PageKey, Page> = {
  overview: lazy(() => import('./pages/overview/AllBrands')),
  dashboard: lazy(() => import('./pages/dashboard/BrandDashboard')),
  opportunities: lazy(() => import('./pages/opportunities/Opportunities')),
  reports: lazy(() => import('./pages/reports/Reports')),
  rankings: lazy(() => import('./pages/rankings/Rankings')),
  backlinks: lazy(() => import('./pages/backlinks/Backlinks')),
  audit: lazy(() => import('./pages/audit/Audit')),
  local: lazy(() => import('./pages/local/Local')),
  searchConsole: lazy(() => import('./pages/search-console/SearchConsole')),
  gaps: lazy(() => import('./pages/gaps/Gaps')),
  content: lazy(() => import('./pages/content/Content')),
  ai: lazy(() => import('./pages/ai/AiOverview')),
  prompts: lazy(() => import('./pages/ai/Prompts')),
  answers: lazy(() => import('./pages/ai/Answers')),
  sources: lazy(() => import('./pages/ai/Sources')),
  mentions: lazy(() => import('./pages/ai/Mentions')),
  studio: lazy(() => import('./pages/studio/ContentPlan')),
  topics: lazy(() => import('./pages/studio/TopicMap')),
  voice: lazy(() => import('./pages/studio/Voice')),
  draftCheck: lazy(() => import('./pages/studio/DraftCheck')),
  facts: lazy(() => import('./pages/ai/Facts')),
  readiness: lazy(() => import('./pages/ai/Readiness')),
  traffic: lazy(() => import('./pages/ai/Traffic')),
  research: lazy(() => import('./pages/research/Research')),
  explorer: lazy(() => import('./pages/explorer/Explorer')),
  settings: lazy(() => import('./pages/settings/Settings')),
};

export type AppRoute = { page: PageDef; Component: Page };

/** Every screen with its component, in sidebar order. */
export const APP_ROUTES: AppRoute[] = PAGES.map((page) => ({ page, Component: PAGE_COMPONENTS[page.key] }));
